import { useState } from 'react';
import { Link } from 'wouter';
import { toast } from 'sonner';
import { RefreshCw, RotateCw } from 'lucide-react';
import { trpc } from '../lib/trpc';
import { Badge, Button, Card, CardContent, EmptyState, Input, PageSpinner, Select } from '../components/ui';
import { useLocale } from '../contexts/LocaleContext';

const statusBadgeVariant = (s: string): 'success' | 'destructive' | 'warning' =>
  s === 'received' ? 'success' : s === 'failed' ? 'destructive' : 'warning';

export function LeadsPage() {
  const { t } = useLocale();
  const [search, setSearch] = useState('');
  const [formId, setFormId] = useState<number | undefined>();
  const [status, setStatus] = useState<'pending' | 'received' | 'failed' | undefined>();
  const utils = trpc.useUtils();
  const forms = trpc.leads.getFormsIndex.useQuery();
  const list = trpc.leads.list.useQuery({ search: search || undefined, formId, status });
  const retryAll = trpc.leads.retryAllFailed.useMutation({
    onSuccess: (r) => {
      toast.success(t('leads.requeuedFailed', { count: r.requeued }));
      void utils.leads.invalidate();
    },
  });

  const statusLabel = (s: string) =>
    s === 'received'
      ? t('leads.badgeReceived')
      : s === 'failed'
        ? t('leads.badgeFailed')
        : t('leads.badgePending');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('leads.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('leads.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => void list.refetch()}>
            <RefreshCw className="h-3.5 w-3.5" /> {t('leads.refresh')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => retryAll.mutate()} disabled={retryAll.isPending}>
            <RotateCw className="h-3.5 w-3.5" /> {t('leads.retryAllFailed')}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="flex flex-wrap gap-3 p-4">
          <Input
            placeholder={t('leads.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs"
          />
          <Select
            value={formId ?? ''}
            onChange={(e) => setFormId(e.target.value ? Number(e.target.value) : undefined)}
            className="max-w-xs"
          >
            <option value="">{t('leads.allForms')}</option>
            {forms.data?.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name} ({f.leadCount})
              </option>
            ))}
          </Select>
          <Select
            value={status ?? ''}
            onChange={(e) => setStatus((e.target.value || undefined) as typeof status)}
            className="max-w-[160px]"
          >
            <option value="">{t('leads.anyStatus')}</option>
            <option value="pending">{t('leads.statusPending')}</option>
            <option value="received">{t('leads.statusReceived')}</option>
            <option value="failed">{t('leads.statusFailed')}</option>
          </Select>
        </CardContent>
      </Card>

      {list.isLoading ? (
        <PageSpinner />
      ) : !list.data?.items.length ? (
        <EmptyState title={t('leads.emptyTitle')} description={t('leads.emptyDescription')} />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 font-medium">{t('leads.colLead')}</th>
                  <th className="px-4 py-2.5 font-medium">{t('leads.colForm')}</th>
                  <th className="px-4 py-2.5 font-medium">{t('leads.colCampaign')}</th>
                  <th className="px-4 py-2.5 font-medium">{t('leads.colStatus')}</th>
                  <th className="px-4 py-2.5 font-medium">{t('leads.colDelivery')}</th>
                  <th className="px-4 py-2.5 font-medium">{t('leads.colReceived')}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((l) => (
                  <LeadRow key={l.id} l={l} statusLabel={statusLabel} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function LeadRow({
  l,
  statusLabel,
}: {
  l: {
    id: number;
    fullName: string | null;
    email: string | null;
    phone: string | null;
    formName: string | null;
    campaignName: string | null;
    status: string;
    delivery: { sent: number; failed: number; pending: number };
    receivedAt: string | Date;
  };
  statusLabel: (s: string) => string;
}) {
  const { t, formatDate, relativeTime } = useLocale();
  return (
    <tr className="border-b last:border-0 hover:bg-accent/40">
      <td className="px-4 py-3">
        <Link href={`/leads/${l.id}`} className="font-medium hover:underline">
          {l.fullName || l.email || l.phone || t('leads.leadNumber', { id: l.id })}
        </Link>
        <div className="text-xs text-muted-foreground">
          {[l.phone, l.email].filter(Boolean).join(' · ')}
        </div>
      </td>
      <td className="px-4 py-3 text-muted-foreground">{l.formName ?? '—'}</td>
      <td className="px-4 py-3 text-muted-foreground">{l.campaignName ?? '—'}</td>
      <td className="px-4 py-3">
        <Badge variant={statusBadgeVariant(l.status)}>{statusLabel(l.status)}</Badge>
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground">
        {l.delivery.sent}✓{' '}
        {l.delivery.failed > 0 && <span className="text-destructive">{l.delivery.failed}✕</span>}{' '}
        {l.delivery.pending > 0 && <span>{l.delivery.pending}…</span>}
      </td>
      <td className="px-4 py-3 text-muted-foreground" title={formatDate(l.receivedAt)}>
        {relativeTime(l.receivedAt)}
      </td>
    </tr>
  );
}

export function LeadDetailPage({ params }: { params: { id: string } }) {
  const { t, formatDate, relativeTime } = useLocale();
  const id = Number(params.id);
  const q = trpc.leads.getDetail.useQuery({ id });
  const utils = trpc.useUtils();
  const retry = trpc.leads.retryLead.useMutation({
    onSuccess: (r) => {
      toast.success(t('leads.requeuedDeliveries', { count: r.requeued }));
      void utils.leads.invalidate();
    },
  });
  if (q.isLoading) return <PageSpinner />;
  if (!q.data) return <EmptyState title={t('leads.notFound')} />;
  const { lead, form, page, deliveries } = q.data;
  const statusLabel = (s: string) =>
    s === 'received'
      ? t('leads.badgeReceived')
      : s === 'failed'
        ? t('leads.badgeFailed')
        : t('leads.badgePending');
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">
            {lead.fullName || lead.email || t('leads.leadNumber', { id: lead.id })}
          </h1>
          <p className="text-sm text-muted-foreground">
            {form?.name} · {page?.name} · {formatDate(lead.receivedAt)}
          </p>
        </div>
        {deliveries.some((d) => d.status === 'failed') && (
          <Button size="sm" onClick={() => retry.mutate({ leadId: id })}>
            <RotateCw className="h-3.5 w-3.5" /> {t('leads.retryFailed')}
          </Button>
        )}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardContent className="p-5">
            <h3 className="mb-3 font-semibold">{t('leads.fieldData')}</h3>
            <dl className="space-y-1.5 text-sm">
              {(lead.fieldData ?? []).map((f, i) => (
                <div key={i} className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">{f.name}</dt>
                  <dd className="text-right font-medium">{f.values.join(', ')}</dd>
                </div>
              ))}
            </dl>
            <h3 className="mb-2 mt-5 font-semibold">{t('leads.adAttribution')}</h3>
            <dl className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">{t('leads.campaign')}</dt>
                <dd>{lead.campaignName ?? '—'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">{t('leads.adSet')}</dt>
                <dd>{lead.adsetName ?? '—'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">{t('leads.ad')}</dt>
                <dd>{lead.adName ?? '—'}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <h3 className="mb-3 font-semibold">{t('leads.deliveries')}</h3>
            {deliveries.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('leads.noDeliveries')}</p>
            ) : (
              <div className="space-y-2">
                {deliveries.map((d) => (
                  <div key={d.id} className="rounded-lg border p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">CRM</span>
                      <Badge variant={statusBadgeVariant(d.status)}>{statusLabel(d.status)}</Badge>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {t('leads.attempts', { count: d.attempts })}
                      {d.lastError ? ` · ${d.lastError}` : ''}
                      {d.sentAt ? ` · ${t('leads.sentAgo', { when: relativeTime(d.sentAt) })}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <h3 className="mb-2 mt-5 font-semibold">{t('leads.rawPayload')}</h3>
            <pre className="max-h-64 overflow-auto rounded-lg bg-muted p-3 text-xs">
              {JSON.stringify(lead.rawPayload ?? {}, null, 2)}
            </pre>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
